
import clsx from "clsx";
import type { ReactNode } from "react";

/* ── Helpers ────────────────────────────────────────── */

export const hasValue = (val: unknown): boolean => {
    if (val === null || val === undefined) return false;
    if (typeof val === "string") return val.trim() !== "";
    if (typeof val === "number") return !Number.isNaN(val);
    if (typeof val === "boolean") return true;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "object") return Object.values(val).some((v) => hasValue(v));
    return false;
};

/* ── Conditional section ────────────────────────────── */

export const ConditionalSection = ({
    show,
    children,
}: {
    show: boolean;
    children: ReactNode;
}) => {
    if (!show) return null;
    return <>{children}</>;
};

/* ── Section title (underlined heading) ─────────────── */

export const SectionTitle = ({ children }: { children: ReactNode }) => (
    <h2 className="text-[17px] font-semibold mt-5 mb-4 underline underline-offset-1 decoration-1">
    {children}
  </h2>
);

export const NextStepsCard = ({ children }: { children: ReactNode }) => (
  <div className="bg-[#ECEFF3] rounded-2xl px-5 py-3 mb-5">
    {children}
  </div>
);

/* ── Status badge ───────────────────────────────────── */

type BadgeVariant = "success" | "warning" | "danger" | "default";

const getStatusVariant = (status: string): BadgeVariant => {
    const lower = status.toLowerCase();
    if (lower.includes("up to date") || lower === "good") return "success";
    if (lower.includes("at risk") || lower === "guarded") return "warning";
    if (lower.includes("missing") || lower === "poor" || lower === "critical") return "danger";
    return "default";
};

export const StatusBadge = ({ status,size }: { status: string,size: "sm" | "md" }) => {
    const variant = getStatusVariant(status);
    return (
        <span
            className={clsx(
                "inline-block px-2 py-0.5 font-normal rounded",
                size === "sm" ? "text-[10px]" : "text-sm",
                variant === "success" && "bg-emr-success-bg text-emr-success",
                variant === "warning" && "bg-emr-warning-bg text-emr-warning",
                variant === "danger" && "bg-emr-danger-bg text-emr-danger",
                variant === "default" && "bg-muted text-muted-foreground"
            )}
        >
            {status}
        </span>
    );
};

/* ── Info grid (3 cols) ─────────────────────────────── */

export const InfoGrid = ({ children }: { children: ReactNode }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 mb-2 ">
        {children}
    </div>
);

/* ── Single info item ───────────────────────────────── */

export const InfoItem = ({
    label,
    value,
}: {
    label: string;
    value: string | number | undefined;
}) => {
    if (!hasValue(value)) return null;
    return (
     <div className="text-[15px] sm:text-[15px] leading-5 break-words">
            <span className="font-medium ">{label}: </span>
            <span className="font-normal">{String(value)}</span>
        </div>
    );
};

/* ── Full-width info row ────────────────────────────── */

export const InfoRow = ({
    label,
    value,
    preserveNewlines = false,
    size = "md",
}: {
    label: string;
    value: string | undefined;
    preserveNewlines?: boolean;
    size?: "sm" | "md" ;
}) => {
    if (!hasValue(value)) return null;
    return (
        <div className={clsx("py-3", size === "sm" ? "text-[10px]" : "text-[15px]")}>
            <span className="font-medium text-foreground">{label}: </span>
            <span
                className={clsx(
                    "font-normal text-foreground",
                    preserveNewlines && "whitespace-pre-line"
                )}
            >
                {value}
            </span>
        </div>
    );
};

/* ── Data table ─────────────────────────────────────── */

export interface TableColumn {
    key: string;
    header: string;
    className?: string;
    render?: (value: any, row: any) => ReactNode;
}

export const EmrTable = ({
  columns,
  data,
}: {
  columns: TableColumn[];
  data: any[];
}) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="overflow-x-auto  my-3">
      <table className="w-full border-collapse text-[15px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  "bg-[#E2E4E8] px-3 py-2 text-left font-medium text-[16px]",
                  col.className
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={clsx(
                    "px-3 py-3 border-b border-gray-300 align-top",
                    col.className
                  )}
                >
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


/* ── Instructions box (amber left border) ───────────── */

export const InstructionsBox = ({ children }: { children: ReactNode }) => (
  <div className="bg-[#FFECCB] rounded-2xl py-3 px-3  my-4 border-l-[6px] border-[#F59E0B]">
    {children}
  </div>
);

/* ── Follow-up card ─────────────────────────────────── */

export const FollowUpCard = ({
    label,
    value,
    size = "md",
}: {
    label: string;
    value: string | undefined;
    size?: "sm" | "md" ;
}) => {
    if (!hasValue(value)) return null;
    return (
        <div className="bg-card py-3 px-5 rounded-md">
            <div className="font-medium text-[14px] text-muted-foreground text-gray-600 mb-1">{label}</div>
            <div className="text-[15px] text-card-foreground">{value}</div>
        </div>
    );
};

/* ── Next steps box ─────────────────────────────────── */

export const NextStepsBox = ({ children }: { children: ReactNode }) => (
    <div className="bg-card p-4 rounded-lg my-3">{children}</div>
);
